import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { getCallLog, getCallLogDownload, getCurrentPhoneNumbers, getSippeers, getInboundRoutes } from "@/api/stackApi";
import { HELP_LOGS_CALLS } from "@/constants/HelpUrls";
import FabBar from "@/design/components/FabBar";
import MessageStack from "@/design/components/MessageStack";
import Page from "@/design/layouts/Page";
import { showSnackbar } from "@/store/reducers/app";
import { setHelpUrl } from "@/store/reducers/ui";
import useNow from "@/utils/hooks/useNow";
import Valkyrie, {
  viArrowDownToLine,
  viCalendar,
  viCircleXmark,
  viPeople,
  viPerson,
  viPhoneClock,
  viSimCard,
  viSpinnerThird,
} from "@sippy-platform/valkyrie";

import { Box, Button, Card, CircularProgress, Container, Divider, Pagination, Paper, Tooltip } from "@mui/material";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  differenceInDays,
  differenceInSeconds,
  endOfDay,
  format,
  isValid,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";
import fileDownload from "js-file-download";

import ClearFilterAction from "../_Components/ClearFilterAction";
import DateFilter from "../_Components/DateFilter";
import FilterBar from "../_Components/FilterBar";
import FilterDropdown from "../_Components/FilterDropdown";
import RefreshActions from "../_Components/RefreshActions";
import TableHead from "../_Components/TableHead";
import TableHeading from "../_Components/TableHeading";
import CallItem from "./CallItem";

export default function CallLog() {
  const dispatch = useDispatch();

  const dateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";

  const [searchParams, setSearchParams] = useSearchParams();

  const pbx = useSelector((state) => state.domain.pbx);
  const logSettings = useSelector((state) => state.settings.logs);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_LOGS_CALLS));
  }, [dispatch]);

  // Filter preparations
  const sippeersQuery = useQuery({
    queryKey: [pbx.context, "sippeers"],
    queryFn: getSippeers,
    refetchInterval: 10 * 1000 * 60,
  });
  const phoneNumbersQuery = useQuery({
    queryKey: [pbx.context, "phoneNumbers.current"],
    queryFn: getCurrentPhoneNumbers,
    refetchInterval: 10 * 1000 * 60,
  });
  const inboundRoutesQuery = useQuery({
    queryKey: [pbx.context, "inboundRoutes"],
    queryFn: getInboundRoutes,
    refetchInterval: 10 * 1000 * 60,
  });

  const userOptions = useMemo(
    () => [
      ...(sippeersQuery.data || []),
      ...(phoneNumbersQuery.data || []).map((phoneNumber) => ({
        did: phoneNumber.did,
        name: (inboundRoutesQuery.data || []).find((inbound) => inbound.did === phoneNumber.did)?.name || undefined,
      })),
    ],
    [sippeersQuery.data, phoneNumbersQuery.data, inboundRoutesQuery.data],
  );

  // Filters
  const [userMode, setUserMode] = useState("unified");
  const now = useNow();

  const { page, order, orderBy, startDate, endDate, user, fromUser, toUser } = useMemo(
    () => ({
      page: Number(searchParams.get("page") ?? 0),
      order: searchParams.get("order") === "asc" ? "asc" : "desc",
      orderBy: ["calldate", "clid", "src", "dst", "billsec"].includes(searchParams.get("orderBy"))
        ? searchParams.get("orderBy")
        : "calldate",
      user: userOptions.find((user) => searchParams.get("user") === (user.value ?? user.exten ?? user.did))
        ? searchParams.get("user")
        : "",
      fromUser: userOptions.find((user) => searchParams.get("fromUser") === (user.value ?? user.exten ?? user.did))
        ? searchParams.get("fromUser")
        : "",
      toUser: userOptions.find((user) => searchParams.get("toUser") === (user.value ?? user.exten ?? user.did))
        ? searchParams.get("toUser")
        : "",
      startDate:
        searchParams.get("startDate") && isValid(parseISO(searchParams.get("startDate")))
          ? searchParams.get("startDate")
          : format(startOfDay(now), dateFormat),
      endDate:
        searchParams.get("endDate") && isValid(parseISO(searchParams.get("endDate")))
          ? searchParams.get("endDate")
          : format(endOfDay(now), dateFormat),
    }),
    [now, searchParams, userOptions],
  );

  const isFiltering = useMemo(
    () =>
      !(
        userMode === "unified" &&
        page === 0 &&
        order === "desc" &&
        orderBy === "calldate" &&
        startDate === format(startOfDay(now), dateFormat) &&
        endDate === format(endOfDay(now), dateFormat) &&
        user === "" &&
        fromUser === "" &&
        toUser === ""
      ),
    [now, userMode, page, order, orderBy, startDate, endDate, user, fromUser, toUser],
  );

  const setFilter = useCallback(
    (key, value, resetPage = true) => {
      searchParams.set(key, value);
      if (resetPage) searchParams.set("page", 0);

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  // Check if the selected date range is invalid (either more than 31 days + 1 hour (because DST) or non-positive duration)
  const invalidDateRange =
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) > 31 * 24 * 60 * 60 + 60 * 60 ||
    differenceInSeconds(parseISO(endDate), parseISO(startDate)) <= 0;

  // Query
  const pauseRefresh = !isWithinInterval(now, { start: parseISO(startDate), end: parseISO(endDate) });
  const callLogQuery = useQuery({
    queryKey: [pbx.context, "log.calls", page, order, orderBy, startDate, endDate, user, fromUser, toUser],
    queryFn: () =>
      getCallLog({
        page,
        order,
        orderBy,
        startDate,
        endDate,
        extension: user,
        from: fromUser,
        to: toUser,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: pauseRefresh ? 0 : logSettings.refreshInterval * 1000 * 60,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: pauseRefresh || logSettings.refreshInterval === 0 ? false : true,
    enabled:
      isValid(parseISO(startDate)) &&
      isValid(parseISO(endDate)) &&
      differenceInDays(parseISO(endDate), parseISO(startDate)) <= 31,
  });

  const callLogDownloadMutation = useMutation({
    mutationFn: () =>
      getCallLogDownload({
        page,
        order,
        orderBy,
        startDate,
        endDate,
        extension: user,
        from: fromUser,
        to: toUser,
      }),
    onMutate: () => dispatch(showSnackbar("We're preparing your download. This may take a while...")),
    onSuccess: (response) => fileDownload(response.data, "cdr.csv"),
  });

  // Handlers
  const handlePage = (event, page) => {
    setFilter("page", page - 1, false);
  };

  return (
    <Page sx={{ pt: 8, position: "relative" }}>
      <title>{`Call log \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <FilterBar>
        <ClearFilterAction disabled={isFiltering} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <DateFilter startDate={startDate} endDate={endDate} setFilter={setFilter} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <Tooltip title={userMode === "unified" ? "Match one user on from or to" : "Match multiple users"}>
          <Button
            color="inherit"
            onClick={() => {
              setUserMode((mode) => (mode === "unified" ? "detached" : "unified"));
              setFilter("user", "");
              setFilter("fromUser", "");
              setFilter("toUser", "");
            }}
            selected={userMode}
          >
            <Valkyrie icon={userMode === "unified" ? viPerson : viPeople} />
          </Button>
        </Tooltip>
        {userMode === "unified" ? (
          <FilterDropdown
            id="user"
            icon={viPerson}
            options={userOptions}
            defaultOptions={[{ name: "All users", value: "" }]}
            value={user}
            setValue={(value) => setFilter("user", value)}
            getOptionLabel={(x) => x.name ?? x.did}
            getOptionLabelSecondary={(x) => x.exten ?? (x.name ? (x.did ?? "") : "")}
            getOptionValue={(x) => x.value ?? x.exten ?? x.did}
            getAvatarIcon={(x) => (x.did ? viSimCard : undefined)}
            searchKeys={["name", "exten", "did"]}
          />
        ) : (
          <>
            <FilterDropdown
              id="from"
              label="From"
              icon={viPerson}
              options={userOptions}
              defaultOptions={[{ name: "All users", value: "" }]}
              value={fromUser}
              setValue={(value) => setFilter("fromUser", value)}
              getOptionLabel={(x) => x.name ?? x.did}
              getOptionLabelSecondary={(x) => x.exten ?? (x.name ? (x.did ?? "") : "")}
              getOptionValue={(x) => x.value ?? x.exten ?? x.did}
              getAvatarIcon={(x) => (x.did ? viSimCard : undefined)}
              searchKeys={["name", "exten", "did"]}
            />
            <FilterDropdown
              id="to"
              label="To"
              icon={viPerson}
              options={userOptions}
              defaultOptions={[{ name: "All users", value: "" }]}
              value={toUser}
              setValue={(value) => setFilter("toUser", value)}
              getOptionLabel={(x) => x.name ?? x.did}
              getOptionLabelSecondary={(x) => x.exten ?? (x.name ? (x.did ?? "") : "")}
              getOptionValue={(x) => x.value ?? x.exten ?? x.did}
              getAvatarIcon={(x) => (x.did ? viSimCard : undefined)}
              searchKeys={["name", "exten", "did"]}
            />
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={() => callLogDownloadMutation.mutate()}
          disabled={callLogDownloadMutation.isPending}
          color="inherit"
          startIcon={
            <Valkyrie
              icon={callLogDownloadMutation.isPending ? viSpinnerThird : viArrowDownToLine}
              spin={callLogDownloadMutation.isPending}
            />
          }
        >
          Download
        </Button>
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <RefreshActions
          onClick={() => callLogQuery.refetch()}
          isRefreshing={callLogQuery.isFetching}
          isPaused={pauseRefresh}
        />
      </FilterBar>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            // Icon, columns x6, recordings
            gridTemplateColumns: "min-content repeat(6, auto) repeat(2, min-content)",
            gridTemplateRows: "auto auto",
            gap: 1,
          }}
        >
          <TableHead>
            <Box sx={{ pr: 0 }} />
            <TableHeading id="calldate" setFilter={setFilter} order={order} orderBy={orderBy}>
              Time
            </TableHeading>
            <TableHeading id="clid" setFilter={setFilter} order={order} orderBy={orderBy}>
              From
            </TableHeading>
            <TableHeading id="src" setFilter={setFilter} order={order} orderBy={orderBy}>
              Number
            </TableHeading>
            <TableHeading id="dst" setFilter={setFilter} order={order} orderBy={orderBy}>
              To
            </TableHeading>
            <TableHeading align="right">Ring time</TableHeading>
            <TableHeading align="right" id="billsec" setFilter={setFilter} order={order} orderBy={orderBy}>
              Talk time
            </TableHeading>
            <TableHeading>Recordings</TableHeading>
            <TableHeading>Actions</TableHeading>
          </TableHead>
          {invalidDateRange ? (
            <MessageStack icon={<Valkyrie icon={viCalendar} />} message="The current date range is not valid." />
          ) : callLogQuery.isFetched ? (
            <>
              {callLogQuery.data?.cdr?.length >= 1 ? (
                <Card sx={{ display: "grid", gridTemplateColumns: "subgrid", gridColumn: "1 / -1", mb: 6.5 }}>
                  {callLogQuery.data?.cdr.map((callLogEvent) => (
                    <CallItem key={callLogEvent.id} callLogEvent={callLogEvent} />
                  ))}
                </Card>
              ) : (
                <MessageStack icon={<Valkyrie icon={viPhoneClock} />} message="No data for your current filters..." />
              )}
            </>
          ) : callLogQuery.isError && !callLogQuery.isFetching ? (
            <MessageStack
              icon={<Valkyrie icon={viCircleXmark} />}
              color="error"
              message="We couldn't finish your request..."
              description={callLogQuery?.error?.message}
            />
          ) : (
            <MessageStack icon={<CircularProgress size={36} />} message="Getting your data..." />
          )}
        </Box>

        <FabBar sx={{ px: 1, justifyContent: "center" }}>
          <Paper
            variant="acrylic"
            sx={{
              whiteSpace: "nowrap",
              display: {
                xs: "none",
                md: "flex",
              },
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 0.75,
            }}
          >
            <Box>
              {page * 50 + 1}-{(page + 1) * 50 > callLogQuery.data?.count ? callLogQuery.data?.count : (page + 1) * 50}{" "}
              of {callLogQuery.data?.count}
            </Box>
          </Paper>
          {Math.ceil(callLogQuery.data?.count / 50) > 1 && (
            <Pagination count={Math.ceil(callLogQuery.data?.count / 50)} page={page + 1} onChange={handlePage} />
          )}
        </FabBar>
      </Container>
    </Page>
  );
}
